import { MuiAlert, Spin, strIsNullOrEmpty } from '@fox/atoms';
import { ResponsiveTable, SortState } from '@fox/data-grid';
import { FlexRowSpaced, FlexWrapRowSpacedCentered } from '@fox/layout';
import { useSubscribeTopicWithHandler } from 'fox-extensions';
import * as moment from 'moment';
import { useState } from 'react';
import styled from 'styled-components';
import { useContentContext } from '../../../content-context';
import { REFETCH_RECEIPTS } from '../../../topics';
import { useReceipts } from '../../query-hooks/query-implementations';
import { Receipt } from '../contracts';
import { ReceiptStatusChip, ReceiptStatusIcon, dateTimeFormat, formatCurrency, getReceiptTotal } from '../shared';
import {
	AllFailedReceiptsDrawer,
	FailedReceiptsDrawer,
	FailedSingleReceiptDrawer,
	FailedSingleReceiptDrawerMobile
} from './failed-receipts';
import { ReceiptDetailsDesktop, ReceiptDetailsMobile } from './receipt-details';
import { useTableSettingsContext } from '../../../table-settings-context';

export const TableWrapper = styled.div`
	width: 100%;
`;

export function ReceiptsTable({
	useLinkedOrganisationData,
	allowedProviders,
	rowsExpandedOnSmallScreens,
	enableRetry
}: {
	useLinkedOrganisationData: boolean;
	rowsExpandedOnSmallScreens: boolean;
	allowedProviders?: string[];
	enableRetry?: boolean;
}) {
	const { requestToken, contentId } = useContentContext();
	const { error: tableSettingsError, getTableSettings, loading: tableSettingsLoading } = useTableSettingsContext();
	const [sort, setSort] = useState<SortState>({ column: 'createdOn', asc: false });
	const [page, setPage] = useState<number>(1);
	const PAGE_SIZE = 10;
	const { error, data, loading, refetch } = useReceipts({
		requestToken,
		contentId,
		page,
		pageSize: PAGE_SIZE,
		sort,
		useLinkedOrganisationData,
		triggers: ['sort', 'page'],
		allowedProviders
	});

	useSubscribeTopicWithHandler(REFETCH_RECEIPTS, () => refetch());

	if (!strIsNullOrEmpty(error)) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (!strIsNullOrEmpty(tableSettingsError)) return <MuiAlert severity="error">{tableSettingsError}</MuiAlert>;

	const tableSettings = getTableSettings('Receipts');

	if (!tableSettings) return <></>;

	const columns = [
		{
			title: 'Payment date',
			dataIndex: 'createdOn',
			render: row => {
				return moment.utc(row.createdOn).local().format(dateTimeFormat);
			},
			mobileRender: row => moment.utc(row.createdOn).local().format('DD/MM/YYYY'),
			showOnMobileRow: true,
			sortable: true
		},
		{ title: 'Receipt number', dataIndex: 'receiptNo' },
		{
			title: 'Payment total',
			dataIndex: 'paymentTotal',
			render: record => {
				return formatCurrency(getReceiptTotal(record), record?.currency);
			}
		},
		{
			title: 'Type',
			dataIndex: 'type',
			render: record => {
				return Array.from(new Set(record.lineItems.map(l => l?.lineType?.name))).join(', ');
			}
		},
		{
			title: 'Payment status',
			dataIndex: 'status',
			showOnMobileRow: true,
			render: r => <ReceiptStatusChip receipt={r} />,
			mobileHeaderRender: record => <ReceiptStatusIcon receipt={record} />
		},
		{
			isActionColumn: true,
			title: 'Actions'
		}
	];

	const columnsToUse = columns.filter(c => {
		const columnSettings = tableSettings.columnSettings.find(s => s.dataIndex === c.dataIndex);
		if (c.isActionColumn) {
			return true;
		} else {
			return columnSettings.isVisible;
		}
	});

	return (
		<TableWrapper>
			<Spin spinning={loading || !data}>
				{data?.items?.length === 0 ? (
					<MuiAlert severity="info">No receipts data to display</MuiAlert>
				) : (
					<ResponsiveTable<Receipt>
						data={data?.items}
						rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
						sortProps={{ onSort: sortState => setSort(sortState), sort }}
						rowIdentifier={enableRetry ? 'id' : undefined}
						rowActionComponent={record => {
							const canRetry =
								enableRetry && !record.success && !record.refunded && !record.pending && record.status !== 'Replaced';

							return (
								<FlexWrapRowSpacedCentered>
									<ReceiptDetailsDesktop
										record={record}
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
									{canRetry ? (
										<FailedSingleReceiptDrawer
											data={record}
											useLinkedOrganisationData={useLinkedOrganisationData}
											allowedProviders={allowedProviders}
										/>
									) : null}
								</FlexWrapRowSpacedCentered>
							);
						}}
						mobileRowActionComponent={record => {
							const canRetry =
								enableRetry && !record.success && !record.refunded && !record.pending && record.status !== 'Replaced';

							return (
								<FlexWrapRowSpacedCentered>
									<ReceiptDetailsMobile
										record={record}
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
									{canRetry ? (
										<FailedSingleReceiptDrawerMobile
											data={record}
											useLinkedOrganisationData={useLinkedOrganisationData}
											allowedProviders={allowedProviders}
										/>
									) : null}
								</FlexWrapRowSpacedCentered>
							);
						}}
						selectedRowActionComponent={(data, selectedRows) => {
							if (!enableRetry) {
								return null;
							}

							return (
								<FlexRowSpaced>
									<AllFailedReceiptsDrawer
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
									<FailedReceiptsDrawer
										data={data}
										selectedRows={selectedRows}
										useLinkedOrganisationData={useLinkedOrganisationData}
										allowedProviders={allowedProviders}
									/>
								</FlexRowSpaced>
							);
						}}
						rowShowSelectorEvaluatorFunction={record => {
							if (!enableRetry) return false;
							return !record.success && !record.refunded && !record.pending && record.status !== 'Replaced';
						}}
						paginationProps={
							data?.totalResults
								? {
										page,
										onPageChange: page => setPage(page),
										total: data?.totalResults,
										itemsPerPage: PAGE_SIZE
								  }
								: undefined
						}
						columns={columnsToUse}
					/>
				)}
			</Spin>
		</TableWrapper>
	);
}
